<section class="console-shell" [class.console-loading]="loading">
  <header class="console-header">
    <div class="brand">
      <mat-icon [icIcon]="faTerminal"></mat-icon>
      <div>
        <h1>Global Console</h1>
        <span>{{ accessVerified ? (workspace?.Name || (loading ? 'Creating workspace…' : 'Select a target to begin')) : 'Administrator verification' }}</span>
      </div>
    </div>

    <nav class="mode-switch" aria-label="Console mode" *ngIf="accessVerified">
      <button *ngFor="let item of modes" type="button" class="mode-button"
        [class.active]="mode === item.value" [attr.aria-pressed]="mode === item.value" (click)="selectMode(item.value)">
        {{ item.label }}
      </button>
    </nav>

    <div class="header-actions">
      <button *ngIf="accessVerified" mat-icon-button type="button" (click)="historyOpen = !historyOpen"
        matTooltip="Workspace history" aria-label="Workspace history">
        <mat-icon [icIcon]="icHistory"></mat-icon>
      </button>
      <button mat-icon-button type="button" class="close-button" (click)="onClose()"
        matTooltip="Close Global Console" aria-label="Close Global Console">
        <mat-icon [icIcon]="icClose"></mat-icon>
      </button>
    </div>
  </header>

  <main class="access-gate" *ngIf="!accessVerified">
    <section class="access-card" aria-labelledby="console-access-title">
      <div class="access-icon"><mat-icon [icIcon]="faTerminal"></mat-icon></div>
      <h2 id="console-access-title">Verify privileged console access</h2>
      <p class="access-summary">Confirm your identity before starting this privileged session. Verification is required each time Global Console opens.</p>

      <div class="risk-notice">
        <strong>Before you continue</strong>
        <p>Commands can change or disrupt customer devices. Output may contain sensitive information. Raw output is live-only; session and command activity is audited.</p>
      </div>

      <mat-checkbox [(ngModel)]="riskAccepted" color="primary" [disabled]="accessLoading || !!accessChallenge">
        I am authorized to access these systems and accept responsibility for my commands.
      </mat-checkbox>

      <mat-form-field *ngIf="isLocalUser && !canBypassMfa && !accessChallenge" appearance="outline" class="access-field">
        <mat-label>Current password</mat-label>
        <input matInput type="password" autocomplete="current-password" [(ngModel)]="localPassword"
          (keydown.enter)="beginAccessVerification()" />
      </mat-form-field>

      <div class="split-code" *ngIf="accessChallenge?.Provider === 'LocalSplitEmailSms'">
        <p>Enter both halves of the verification code. Email: {{ accessChallenge.MaskedEmail }} · SMS: {{ accessChallenge.MaskedPhone }}</p>
        <mat-form-field appearance="outline">
          <mat-label>Email code</mat-label>
          <input matInput inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]*" [(ngModel)]="emailCode" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>SMS code</mat-label>
          <input matInput inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]*" [(ngModel)]="smsCode"
            (keydown.enter)="verifyLocalAccess()" />
        </mat-form-field>
      </div>

      <p class="access-error" *ngIf="accessError" role="alert">{{ accessError }}</p>
      <button *ngIf="!accessChallenge" mat-flat-button color="primary" type="button"
        [disabled]="!riskAccepted || accessLoading || (isLocalUser && !canBypassMfa && !localPassword)" (click)="beginAccessVerification()">
        {{ canBypassMfa ? 'Open console' : (isLocalUser ? 'Send email and SMS codes' : 'Verify with Microsoft Entra') }}
      </button>
      <button *ngIf="accessChallenge?.Provider === 'LocalSplitEmailSms'" mat-flat-button color="primary" type="button"
        [disabled]="accessLoading || emailCode.length !== 6 || smsCode.length !== 6" (click)="verifyLocalAccess()">
        Verify and open console
      </button>
      <button *ngIf="accessChallenge?.Provider === 'MicrosoftEntra'" mat-flat-button color="primary" type="button"
        [disabled]="accessLoading" (click)="verifyWithEntra()">Retry Microsoft Entra MFA</button>
      <div class="access-progress" *ngIf="accessLoading" aria-live="polite">
        <mat-spinner diameter="20"></mat-spinner><span>Verifying privileged access...</span>
      </div>
    </section>
  </main>

  <div class="console-body" *ngIf="accessVerified">
    <aside class="workspace-sidebar" [class.history-visible]="historyOpen">
      <ng-container *ngIf="historyOpen; else workspaceTools">
        <div class="sidebar-heading">
          <span>History</span>
          <button mat-icon-button (click)="loadHistory(0)" [disabled]="historyLoading" matTooltip="Refresh history" aria-label="Refresh history">
            <mat-spinner *ngIf="historyLoading" diameter="18"></mat-spinner>
            <mat-icon *ngIf="!historyLoading" [icIcon]="icRefresh"></mat-icon>
          </button>
        </div>
        <button type="button" class="history-row" *ngFor="let item of workspaces; trackBy: trackById"
          [class.active]="item.Id === workspace?.Id" (click)="openWorkspace(item)">
          <strong [matTooltip]="item.Name">{{ item.Name }}</strong>
          <span>{{ modes[item.Mode]?.label }} · {{ item.LastActivityAt | date:'short' }}</span>
        </button>
        <p class="empty-copy" *ngIf="!historyLoading && !workspaces.length">No console sessions on this page.</p>
        <mat-paginator *ngIf="historyTotalCount > 0" class="history-paginator"
          [length]="historyTotalCount" [pageIndex]="historyPageIndex" [pageSize]="historyPageSize"
          [pageSizeOptions]="[5, 10, 25]" [disabled]="historyLoading" showFirstLastButtons
          (page)="onHistoryPage($event)" aria-label="Console history pages">
        </mat-paginator>
      </ng-container>

      <ng-template #workspaceTools>
        <ng-container *ngIf="mode === GlobalConsoleMode.Device; else automationFiles">
          <div class="sidebar-heading"><span>Targets</span></div>
          <mat-form-field appearance="outline" class="device-picker">
            <mat-label>Managed devices</mat-label>
            <mat-select multiple [ngModel]="selectedDeviceIds" (ngModelChange)="onDeviceSelectionChange($event)"
              [disabled]="freeSsh || targetSyncing || loading">
              <mat-select-trigger>
                {{ selectedDeviceIds.length }} {{ selectedDeviceIds.length === 1 ? 'device' : 'devices' }} selected
              </mat-select-trigger>
              <mat-option>
                <ngx-mat-select-search [formControl]="deviceSearchCtrl" [searching]="devicesLoading"
                  placeholderLabel="Search by device name or IP"
                  noEntriesFoundLabel="No connected devices found">
                </ngx-mat-select-search>
              </mat-option>
              <mat-option *ngFor="let device of devices" [value]="device.Value || device.Id">
                {{ deviceLabel(device) }}
              </mat-option>
              <mat-option *ngIf="!devicesLoading && !devices.length" disabled>No connected devices available</mat-option>
            </mat-select>
            <mat-spinner matSuffix *ngIf="devicesLoading" diameter="18" aria-label="Loading connected devices"></mat-spinner>
          </mat-form-field>
          <mat-checkbox [ngModel]="freeSsh" (ngModelChange)="onFreeSshChange($event)" [disabled]="targetSyncing || loading" color="primary">
            Free SSH console
          </mat-checkbox>
          <p class="hint" *ngIf="freeSsh">Credentials typed at an SSH prompt are not retained.</p>
          <div class="target-state" [class.syncing]="targetSyncing || devicesLoading" aria-live="polite">
            <mat-spinner *ngIf="targetSyncing || devicesLoading" diameter="16"></mat-spinner>
            <mat-icon *ngIf="!targetSyncing && !devicesLoading" [icIcon]="faTerminal"></mat-icon>
            <span *ngIf="devicesLoading">Loading connected devices…</span>
            <span *ngIf="targetSyncing && !devicesLoading">Updating terminal tabs…</span>
            <span *ngIf="!targetSyncing && !devicesLoading && sessions.length">{{ sessions.length }} terminal {{ sessions.length === 1 ? 'tab' : 'tabs' }} ready</span>
            <span *ngIf="!targetSyncing && !devicesLoading && !sessions.length">Select a connected device to open its terminal automatically</span>
          </div>
        </ng-container>

        <ng-template #automationFiles>
          <div class="prefill-panel">
            <mat-form-field appearance="outline">
              <mat-label>Prefill from</mat-label>
              <mat-select [(ngModel)]="prefillType" (selectionChange)="loadPrefillOptions()">
                <mat-option value="Template">Template</mat-option>
                <mat-option value="Blueprint">Blueprint</mat-option>
                <mat-option value="Inventory">Inventory</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>{{ prefillType }}</mat-label>
              <mat-select [(ngModel)]="prefillId">
                <mat-option *ngFor="let option of prefillOptions" [value]="option.Value || option.Id">{{ deviceLabel(option) }}</mat-option>
              </mat-select>
            </mat-form-field>
            <button mat-icon-button color="primary" (click)="applyPrefill()" [disabled]="!prefillId"
              matTooltip="Prefill workspace" aria-label="Prefill workspace">
              <mat-icon [icIcon]="icDownload"></mat-icon>
            </button>
          </div>
          <div class="sidebar-heading">
            <span>Workspace files</span>
            <div>
              <button mat-icon-button (click)="filePicker.click()" matTooltip="Add files" aria-label="Add files">
                <mat-icon [icIcon]="icUpload"></mat-icon>
              </button>
              <button mat-icon-button (click)="newFile()" matTooltip="New file" aria-label="New file">
                <mat-icon [icIcon]="icAdd"></mat-icon>
              </button>
            </div>
          </div>
          <input #filePicker type="file" hidden multiple
            [accept]="mode === GlobalConsoleMode.Ansible ? '.yml,.yaml,.ini,.json,.txt,.hosts' : '.tf,.tf.json,.tfvars,.tfvars.json,.tfstate'"
            (change)="addFiles($event)" />
          <div class="file-row" role="button" tabindex="0" *ngFor="let file of files; trackBy: trackById"
            [class.active]="file.Id === activeFileId" (click)="selectFile(file)" (keydown.enter)="selectFile(file)">
            <mat-icon [icIcon]="file.Sensitive ? icLock : icFileAlt"></mat-icon>
            <span>{{ file.Name }}</span>
            <button mat-icon-button (click)="deleteFile(file, $event)" matTooltip="Remove file" [attr.aria-label]="'Remove ' + file.Name">
              <mat-icon [icIcon]="icDelete"></mat-icon>
            </button>
          </div>
          <p class="empty-copy" *ngIf="!files.length">Add YAML/hosts or Terraform/state files. Credentials remain your responsibility.</p>
        </ng-template>
      </ng-template>
    </aside>

    <main class="terminal-workspace">
      <div class="session-tabs" role="tablist" aria-label="Terminal sessions">
        <button type="button" role="tab" class="session-tab" *ngFor="let session of sessions; trackBy: trackById"
          [class.active]="session.Id === activeSessionId" [class.closing]="isSessionClosing(session.Id)"
          [attr.aria-selected]="session.Id === activeSessionId"
          (click)="activeSessionId = session.Id">
          <span class="status-dot status-{{ session.Status }}"></span>
          <span>{{ session.DeviceName || session.Name }}</span>
          <small>{{ isSessionClosing(session.Id) ? 'Closing…' : statusLabel(session.Status) }}</small>
          <mat-spinner *ngIf="isSessionClosing(session.Id)" diameter="14"></mat-spinner>
          <mat-icon *ngIf="!isSessionClosing(session.Id)" [icIcon]="icClose" (click)="endSession(session, $event)"></mat-icon>
        </button>
        <span class="tab-placeholder" *ngIf="!sessions.length">Select devices on the left—terminal tabs open automatically</span>
      </div>

      <div class="automation-editor" *ngIf="mode !== GlobalConsoleMode.Device && activeFile">
        <div class="editor-bar">
          <span>{{ activeFile.Name }}<em *ngIf="activeFile.Sensitive">Sensitive</em></span>
          <button mat-icon-button (click)="saveActiveFile()" matTooltip="Save file" aria-label="Save file">
            <mat-icon [icIcon]="icSave"></mat-icon>
          </button>
        </div>
        <ngx-monaco-editor [options]="editorOptions" [(ngModel)]="activeFile.Content"></ngx-monaco-editor>
      </div>

      <section class="terminal-panel" [class.with-editor]="mode !== GlobalConsoleMode.Device && activeFile">
        <div class="terminal-toolbar">
          <span>{{ activeSession?.Name || 'Terminal' }}</span>
          <div>
            <button mat-icon-button (click)="copyOutput()" matTooltip="Copy output" aria-label="Copy output">
              <mat-icon [icIcon]="icFileCopy"></mat-icon>
            </button>
            <button mat-icon-button (click)="downloadOutput()" matTooltip="Download current live output" aria-label="Download current live output">
              <mat-icon [icIcon]="icDownload"></mat-icon>
            </button>
            <button mat-icon-button (click)="clearView()" matTooltip="Clear local view" aria-label="Clear local view">
              <mat-icon [icIcon]="icClear"></mat-icon>
            </button>
          </div>
        </div>
        <pre #terminalOutput class="terminal-output" aria-live="polite"><span>{{ activeOutput || (activeSession ? 'Connected. Enter a command below.' : 'No terminal session is open.') }}</span><span *ngIf="isSessionProcessing(activeSession)" class="terminal-progress" role="status" [ngSwitch]="processingPhase(activeSession)"><span *ngSwitchCase="'dispatching'" class="terminal-status-text dispatching">[dispatch] Sending command to Worker Runtime...</span><span *ngSwitchCase="'queued'" class="terminal-status-text queued">[queued] Waiting for an available worker...</span><span *ngSwitchCase="'connecting'" class="terminal-status-text connecting">[connect] Opening secure device session...</span><span *ngSwitchCase="'running'" class="terminal-status-text running">[running] Command is running...</span><span *ngSwitchCase="'protected-input'" class="terminal-status-text interactive">[ssh] Waiting for protected input...</span><span *ngSwitchCase="'interactive'" class="terminal-status-text interactive">[ssh] Interactive session active</span><span class="terminal-cursor" aria-hidden="true"></span></span></pre>
      </section>

      <footer class="command-bar">
        <mat-checkbox *ngIf="mode === GlobalConsoleMode.Device && sessions.length > 1"
          [(ngModel)]="broadcast" color="primary" matTooltip="Run on every selected device">
          Broadcast to {{ sessions.length }}
        </mat-checkbox>
        <span class="prompt">›</span>
        <input *ngIf="activeSession?.ProtectedInputActive" [(ngModel)]="command"
          [disabled]="!liveConnected || !activeSession || loading || targetSyncing || isCommandUnavailable(activeSession)"
          placeholder="Protected input — not retained" type="password"
          (keydown)="onCommandKeydown($event)" autocomplete="off" spellcheck="false" aria-label="Protected terminal input" />
        <textarea *ngIf="!activeSession?.ProtectedInputActive" [(ngModel)]="command" rows="1"
          [disabled]="!liveConnected || !activeSession || loading || targetSyncing || isCommandUnavailable(activeSession)"
          [placeholder]="activeSession?.Mode === GlobalConsoleMode.FreeSsh && !activeInteractiveExecution[activeSessionId] ? 'ssh admin@192.168.0.1' : 'Enter command'"
          (keydown)="onCommandKeydown($event)" spellcheck="false"
          aria-label="Terminal command; Shift+Enter adds a new line"></textarea>
        <button mat-icon-button color="primary" (click)="run()" [disabled]="!liveConnected || !activeSession || !command.trim() || loading || targetSyncing || isCommandUnavailable(activeSession)"
          matTooltip="Run command" aria-label="Run command">
          <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
          <mat-icon *ngIf="!loading" [icIcon]="icSend"></mat-icon>
        </button>
      </footer>
    </main>
  </div>
</section>
